import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import type { Locale } from './types';
import { DEFAULT_LOCALE, DEFAULT_NAMESPACE, NAMESPACES, SUPPORTED_LOCALES } from './types';

// en-US
import enCommon from './locales/en-US/common.json';
import enAuth from './locales/en-US/auth.json';
import enTimeline from './locales/en-US/timeline.json';
import enSettings from './locales/en-US/settings.json';
import enOnboarding from './locales/en-US/onboarding.json';
import enUpload from './locales/en-US/upload.json';
import enPhoto from './locales/en-US/photo.json';
import enInvite from './locales/en-US/invite.json';
import enProfile from './locales/en-US/profile.json';
import enErrors from './locales/en-US/errors.json';
import enMarketing from './locales/en-US/marketing.json';
import enActivity from './locales/en-US/activity.json';

// fr-CA
import frCommon from './locales/fr-CA/common.json';
import frAuth from './locales/fr-CA/auth.json';
import frTimeline from './locales/fr-CA/timeline.json';
import frSettings from './locales/fr-CA/settings.json';
import frOnboarding from './locales/fr-CA/onboarding.json';
import frUpload from './locales/fr-CA/upload.json';
import frPhoto from './locales/fr-CA/photo.json';
import frInvite from './locales/fr-CA/invite.json';
import frProfile from './locales/fr-CA/profile.json';
import frErrors from './locales/fr-CA/errors.json';
import frMarketing from './locales/fr-CA/marketing.json';
import frActivity from './locales/fr-CA/activity.json';

export interface I18nInitOptions {
  locale?: Locale;
  /** i18next plugin for language detection (e.g. i18next-browser-languagedetector) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  detector?: any;
}

export function initI18n(options: I18nInitOptions = {}): import('i18next').i18n {
  const instance = i18next.createInstance();

  const plugins = [initReactI18next];
  if (options.detector) {
    plugins.unshift(options.detector);
  }

  for (const plugin of plugins) {
    instance.use(plugin);
  }

  instance.init({
    initImmediate: false, // Synchronous init since resources are bundled
    lng: options.detector ? undefined : (options.locale ?? DEFAULT_LOCALE),
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: SUPPORTED_LOCALES,
    ns: NAMESPACES,
    defaultNS: DEFAULT_NAMESPACE,
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: options.detector
      ? {
          order: ['localStorage', 'navigator'],
          lookupLocalStorage: 'wovendays-locale',
          caches: ['localStorage'],
        }
      : undefined,
    resources: {
      'en-US': {
        common: enCommon,
        auth: enAuth,
        timeline: enTimeline,
        settings: enSettings,
        onboarding: enOnboarding,
        upload: enUpload,
        photo: enPhoto,
        invite: enInvite,
        profile: enProfile,
        errors: enErrors,
        marketing: enMarketing,
        activity: enActivity,
      },
      'fr-CA': {
        common: frCommon,
        auth: frAuth,
        timeline: frTimeline,
        settings: frSettings,
        onboarding: frOnboarding,
        upload: frUpload,
        photo: frPhoto,
        invite: frInvite,
        profile: frProfile,
        errors: frErrors,
        marketing: frMarketing,
        activity: frActivity,
      },
    },
  });

  return instance;
}
